import type { ReactNode } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import { useHydrated } from 'remix-utils';

import {
    MagicIsland,
    MagicIslandProvider,
} from '~/ui-rocco/pages/detail-shared';
import type { loader } from '~/routes/_app.product.$urlSegment';

interface ProductDetailsMagicIslandProps {
    children: ReactNode;
}

/**
 * Product-detail wrapper for the Magic Island. Provides the controller context
 * around the page content (so sections can register their island modes) and
 * mounts the shell once hydrated. Gated by the `details_revamp_2026` umbrella
 * flag, with a `?detailsRevamp=1` dev override.
 *
 * When disabled it renders the children untouched and provides no context — the
 * section hooks then no-op, so the page is unaffected. The island is client-only
 * (`useHydrated`), so the server renders nothing and there is no hydration shift.
 *
 * The page-specific knowledge lives here and in the sections' registration
 * calls; the shell, controller and hooks stay page-agnostic, so a future
 * ProjectDetailsMagicIsland is just this wrapper with different sections.
 */
export const ProductDetailsMagicIsland = ({
    children,
}: ProductDetailsMagicIslandProps) => {
    const { detailsRevamp } = useLoaderData<typeof loader>();
    const [searchParams] = useSearchParams();
    const hydrated = useHydrated();

    const enabled =
        detailsRevamp ||
        (import.meta.env.DEV && searchParams.get('detailsRevamp') === '1');

    if (!enabled) return <>{children}</>;

    return (
        <MagicIslandProvider>
            {children}
            {hydrated ? <MagicIsland /> : null}
        </MagicIslandProvider>
    );
};
